import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Loader2, RotateCcw } from 'lucide-react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, CreditDefault, Organization, RateCardRow } from '../lib/api';
import {
  Banner, GrantCreditsForm, LeadBatchForm, NotChargedTag, OrgRateCardTable, groupByFamily, reasonOf,
} from '../components/CreditsKit';

/**
 * Rate card (28 Sep, mig 0702).
 *
 * The owner: "Everything has a credit pricing that's uniform now … We just
 * change the number of credits each thing costs, and this can be overwritten
 * at the organization level." So this page is:
 *   · the GLOBAL card — every priced action (not the five it used to hard-code),
 *     editable in place, with the never-charged rows marked "not charged";
 *   · pick an organisation → its EFFECTIVE card, each row "Default" or
 *     "Agreed for this org", with set / clear;
 *   · for that organisation, add credits (one org-wide lot) or add credits for
 *     a lead batch (leads × credits per lead, typed per batch).
 * Per-agent prices and per-agent grants are gone: every agent draws from its
 * organisation's one balance.
 */
export const Pricing: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState('');

  useEffect(() => { api.getOrganizations().then(setOrgs).catch(() => setOrgs([])); }, [api]);
  const org = orgs.find(o => o.id === orgId);

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center space-x-3">
        <Coins className="w-7 h-7 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Rate card</h1>
      </div>

      <p className="text-sm text-gray-600">
        One balance per organisation. Every thing costs credits from this card; an organisation can have its own number for any row.
      </p>

      <div className="bg-white shadow rounded-lg p-4 flex flex-wrap items-center gap-3">
        <label htmlFor="ratecard-org" className="text-sm font-medium text-gray-700">Showing</label>
        <select
          id="ratecard-org"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          className="block w-full max-w-sm rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="">The default card (every organisation)</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {org && (
          <Link to={`/organizations/${org.id}`} className="text-sm text-blue-600 hover:text-blue-700">
            Open {org.name}'s credits &amp; terms
          </Link>
        )}
      </div>

      {orgId ? <OrgPanel key={orgId} api={api} orgId={orgId} orgName={org?.name} /> : <DefaultsPanel api={api} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The default card
// ---------------------------------------------------------------------------

const DefaultsPanel: React.FC<{ api: AdminAPI }> = ({ api }) => {
  const [defaults, setDefaults] = useState<CreditDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try { setDefaults(await api.getCreditDefaults()); }
    catch (e) { setErr(reasonOf(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const groups = groupByFamily(defaults);

  return (
    <section className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">The default card</h2>
        <p className="mt-1 text-sm text-gray-500">
          What every organisation pays unless it has its own number. Edits apply at once.
        </p>
      </div>
      {err && <div className="p-4"><Banner kind="error">{err}</Banner></div>}
      {loading && defaults.length === 0 ? (
        <div className="px-6 py-4 text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Thing</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Per</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
              <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
            </tr>
          </thead>
          {groups.map(g => (
            <tbody key={g.family} className="bg-white divide-y divide-gray-100">
              <tr className="bg-gray-50/60">
                <th colSpan={4} className="px-6 py-1.5 text-left text-xs font-semibold text-gray-700">{g.label}</th>
              </tr>
              {g.rows.map(d => (
                <DefaultRow
                  key={d.action}
                  row={d}
                  onSave={async (val) => { await api.updateCreditDefault(d.action, val); await refresh(); }}
                />
              ))}
            </tbody>
          ))}
        </table>
      )}
    </section>
  );
};

const DefaultRow: React.FC<{ row: CreditDefault; onSave: (val: number) => Promise<void> }> = ({ row, onSave }) => {
  const [val, setVal] = useState(String(row.credits));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setVal(String(row.credits)); }, [row.credits]);
  const num = Number(val);
  const valid = val !== '' && Number.isInteger(num) && num >= 0;
  const dirty = valid && num !== row.credits;
  const save = async () => {
    setSaving(true); setErr(null);
    try { await onSave(num); } catch (e) { setErr(reasonOf(e)); } finally { setSaving(false); }
  };
  return (
    <tr>
      <td className="px-6 py-2">
        <span className="text-sm font-medium text-gray-900">{row.label || row.action}</span>
        {row.customer_visible === false && <NotChargedTag />}
      </td>
      <td className="px-6 py-2 text-xs text-gray-500">{row.unit || '—'}</td>
      <td className="px-6 py-2">
        <div className="flex items-center space-x-2">
          <input
            type="number" min={0} step={1} value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && dirty) save(); }}
            aria-label={`${row.label || row.action}: default credits`}
            className="w-24 rounded border-gray-300 text-sm tabular-nums focus:border-blue-500 focus:ring-blue-500"
          />
          {dirty && (
            <>
              <button
                disabled={saving}
                onClick={save}
                className="px-3 py-1 text-xs rounded font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setVal(String(row.credits))} className="text-xs text-gray-500 hover:text-gray-700" title="Reset">
                <RotateCcw className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
        {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
      </td>
      <td className="px-6 py-2 text-xs text-gray-500">
        {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
        {row.updated_by ? <span className="block text-gray-400">{row.updated_by}</span> : null}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// One organisation: its effective card, and adding credits
// ---------------------------------------------------------------------------

const OrgPanel: React.FC<{ api: AdminAPI; orgId: string; orgName?: string }> = ({ api, orgId, orgName }) => {
  const [rows, setRows] = useState<RateCardRow[]>([]);
  const [left, setLeft] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<'grant' | 'batch' | null>(null);

  const loadBalance = () =>
    api.getOrgCreditsOverview(orgId)
      .then(o => setLeft(o.credits_left ?? o.balance))
      .catch(() => setLeft(null));

  useEffect(() => {
    setLoading(true); setErr(null);
    Promise.all([api.getEffectiveRateCard(orgId), loadBalance()])
      .then(([card]) => setRows(card))
      .catch(e => setErr(reasonOf(e)))
      .finally(() => setLoading(false));
  }, [orgId]);

  const done = (msg: string) => { setNotice(msg); setOpen(null); loadBalance(); };

  return (
    <section className="space-y-4">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs text-gray-500">Credits left</div>
            <div className="text-2xl font-semibold text-gray-900 tabular-nums">{left == null ? '—' : left.toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setOpen(open === 'batch' ? null : 'batch')} className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">
              {open === 'batch' ? 'Cancel' : 'Add credits for a lead batch'}
            </button>
            <button onClick={() => setOpen(open === 'grant' ? null : 'grant')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              {open === 'grant' ? 'Cancel' : 'Add credits'}
            </button>
          </div>
        </div>
        {open === 'grant' && <div className="mt-4 pt-4 border-t border-gray-100"><GrantCreditsForm api={api} orgId={orgId} orgName={orgName} onDone={done} /></div>}
        {open === 'batch' && <div className="mt-4 pt-4 border-t border-gray-100"><LeadBatchForm api={api} orgId={orgId} orgName={orgName} onDone={done} /></div>}
      </div>

      {notice && <Banner kind="success">{notice}</Banner>}
      {err && <Banner kind="error">{err}</Banner>}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{orgName ? `${orgName}'s card` : 'This organisation’s card'}</h2>
          <p className="mt-1 text-sm text-gray-500">
            Type a number to give this organisation its own price; clear it to go back to the default. The per-lead price and the success fees are its terms.
          </p>
        </div>
        {loading ? (
          <div className="px-6 py-4 text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>
        ) : (
          <OrgRateCardTable api={api} orgId={orgId} rows={rows} onChange={setRows} />
        )}
      </div>
    </section>
  );
};

export default Pricing;
